import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateIntakeStatus,
  linkIntakeToClient,
  createClientFromIntake,
} from "../../store/thunks/intakeThunk";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  LinkIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  XMarkIcon,
  UserPlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

function IntakeDetailsModal({ intake, onClose }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user || []);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showLinkClientModal, setShowLinkClientModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showConfirmLinkModal, setShowConfirmLinkModal] = useState(false);
  const [clientCreated, setClientCreated] = useState(false);
  const [activeSection, setActiveSection] = useState("clientInfo");
  const [createParents, setCreateParents] = useState(true);

  const handleApproveIntake = () => setShowApprovalModal(true);

  const getStatusIcon = (status) => {
    if (status.includes("approved"))
      return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    if (status.includes("rejected"))
      return <XCircleIcon className="h-4 w-4 text-red-500" />;
    return <ClockIcon className="h-4 w-4 text-yellow-500" />;
  };

  const getStatusColor = (status) => {
    if (status.includes("approved"))
      return "bg-green-50 text-green-700 border-green-200";
    if (status.includes("rejected"))
      return "bg-red-50 text-red-700 border-red-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  const getCurrentStatus = () => {
    const latestStatus = intake?.statusHistory[intake.statusHistory.length - 1];
    return latestStatus?.status || "Unknown";
  };

  const handleApprovalSelection = async (status) => {
    if (!intake?._id || !user?._id) return;
    try {
      await dispatch(
        updateIntakeStatus({ id: intake._id, status, userId: user._id })
      ).unwrap();
      setShowApprovalModal(false);
      setClientCreated(true);
    } catch (error) {
      console.error("Error in handleApprovalSelection:", error);
    }
  };

  const handleLinkClient = () => setShowLinkClientModal(true);
  const handleCreateClient = () => setShowCreateClientModal(true);

  const handleClientSelection = (client) => {
    setSelectedClient(client);
    setShowConfirmLinkModal(true);
    setShowLinkClientModal(false);
  };

  const handleConfirmLink = async () => {
    if (!selectedClient || !intake) return;
    try {
      await dispatch(
        linkIntakeToClient({
          intakeId: intake._id,
          clientId: selectedClient._id,
        })
      ).unwrap();
      setShowConfirmLinkModal(false);
      onClose();
    } catch (error) {
      console.error("Error linking client:", error);
    }
  };

  const handleCreateClientFromIntake = async () => {
    try {
      await dispatch(
        createClientFromIntake({
          intakeId: intake._id,
          createParents,
        })
      ).unwrap();
      setShowCreateClientModal(false);
      onClose();
    } catch (error) {
      console.error("Error creating client from intake:", error);
    }
  };

  const renderValue = (value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "object") {
      return (
        <div className="space-y-1.5">
          {Object.entries(value || {}).map(([key, val]) => (
            <div key={key} className="flex items-start gap-2">
              <span className="text-sm font-medium text-gray-500 capitalize min-w-[120px] pt-0.5">
                {key.replace(/([A-Z])/g, " $1").toLowerCase()}:
              </span>
              <span className="text-sm text-gray-700 flex-1">
                {renderValue(val)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return value;
  };

  const renderSection = (title, data, sectionKey) => {
    if (!data || Object.keys(data).length === 0) return null;
    const isActive = activeSection === sectionKey;

    return (
      <div className="border-b border-gray-200 last:border-0">
        <button
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
          onClick={() => setActiveSection(isActive ? null : sectionKey)}>
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              isActive ? "rotate-180" : ""
            }`}
          />
        </button>

        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isActive ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          }`}>
          <div className="px-4 py-3 bg-gray-50/50 space-y-2">
            {Object.entries(data).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2">
                <span className="text-sm font-medium text-gray-500 capitalize min-w-[120px] pt-0.5">
                  {key.replace(/([A-Z])/g, " $1").toLowerCase()}:
                </span>
                <span className="text-sm text-gray-700 flex-1">
                  {renderValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (!intake) return null;

  const currentStatus = getCurrentStatus();
  const statusColorClass = getStatusColor(currentStatus);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-start justify-center sm:items-center p-0 sm:p-4">
        <div className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl bg-white sm:rounded-sm shadow-2xl flex flex-col">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sm:rounded-t-xl">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  Intake Details
                </h2>
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${statusColorClass}`}>
                  {getStatusIcon(currentStatus)}
                  <span className="font-medium capitalize">
                    {currentStatus.replace(/-/g, " ")}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <a
                  href={`${window.location.origin}/intake/${intake._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-sm bg-white hover:text-gray-900 hover:bg-gray-50 ring-1 ring-transparent hover:ring-indigo-600 focus:outline-none transition">
                  <DocumentTextIcon className="h-4 w-4" />
                  View Form
                </a>
                {intake.clientId ? (
                  <div className="text-gray-600 inline-flex items-center gap-1.5">
                    <LinkIcon className="h-4 w-4 text-gray-400" />
                    Linked to {intake.clientId.firstName}{" "}
                    {intake.clientId.lastName}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCreateClient}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-sm bg-white hover:text-gray-900 hover:bg-gray-50 ring-1 ring-transparent hover:ring-indigo-600 focus:outline-none transition">
                      <UserPlusIcon className="h-4 w-4" />
                      Create Client
                    </button>

                    <button
                      onClick={handleLinkClient}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-sm bg-white hover:text-gray-900 hover:bg-gray-50 ring-1 ring-transparent hover:ring-indigo-600 focus:outline-none transition">
                      <LinkIcon className="h-4 w-4" />
                      Link to Client
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-500 rounded-sm hover:bg-gray-100">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="divide-y divide-gray-200">
              {renderSection("Client Information", intake.client, "clientInfo")}
              {renderSection(
                "Parent/Guardian 1",
                intake.parentGuardian1,
                "guardian1"
              )}
              {renderSection(
                "Parent/Guardian 2",
                intake.parentGuardian2,
                "guardian2"
              )}
              {renderSection(
                "Medical History",
                intake.medicalHistory,
                "medical"
              )}
              {renderSection("Daily Program", intake.dailyProgram, "program")}
              {renderSection(
                "Preschool Language",
                intake.preschoolLanguage,
                "language"
              )}
              {renderSection(
                "Articulation Concerns",
                intake.articulationConcerns,
                "articulation"
              )}
              {renderSection(
                "Language Concerns",
                intake.languageConcerns,
                "languageConcerns"
              )}
              {renderSection("Voice Concerns", intake.voiceConcerns, "voice")}
              {renderSection(
                "Fluency Concerns",
                intake.fluencyConcerns,
                "fluency"
              )}
              {renderSection(
                "Hearing Loss Concerns",
                intake.hearingLossConcerns,
                "hearing"
              )}
              {renderSection(
                "Academic/Social Impact",
                intake.academicSocialImpact,
                "academic"
              )}
              {renderSection(
                "Education History",
                intake.educationHistory,
                "education"
              )}
              {renderSection(
                "Consent Agreements",
                intake.consentAgreements,
                "consent"
              )}
              {renderSection(
                "Limits of Confidentiality",
                intake.limitsOfConfidentiality,
                "confidentiality"
              )}
              {renderSection(
                "Cancellation Policy",
                intake.cancellationPolicy,
                "cancellation"
              )}
              {renderSection(
                "Pre-Authorized Payment",
                intake.preAuthorizedPayment,
                "payment"
              )}
              {renderSection(
                "Code of Conduct",
                intake.codeOfConduct,
                "conduct"
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="flex justify-end gap-2 p-4 bg-white border-t border-gray-200 sm:rounded-b-xl">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
              Close
            </button>
            {!intake.statusHistory.some((status) =>
              status.status.includes("approved")
            ) && (
              <button
                onClick={handleApproveIntake}
                className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-700">
                {clientCreated ? "Next" : "Approve Intake"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Approval Selection Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-sm shadow-xl">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Approve Intake
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() =>
                    handleApprovalSelection("approved - no deposit")
                  }
                  className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-sm hover:bg-gray-50">
                  Approved - No Deposit
                </button>
                <button
                  onClick={() =>
                    handleApprovalSelection("approved - deposit received")
                  }
                  className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-sm hover:bg-gray-50">
                  Approved - Deposit Received
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Client Modal */}
      {showLinkClientModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-sm shadow-xl">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Link to Client
                </h3>
                <button
                  onClick={() => setShowLinkClientModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-500 rounded-sm hover:bg-gray-100">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <SearchDropdown
                prefix=":client:"
                displayFields={["firstName", "lastName", "email"]}
                onSelection={handleClientSelection}
                placeholder="Search for a client"
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirm Link Modal */}
      {showConfirmLinkModal && selectedClient && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-sm shadow-xl">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Confirm Link
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Link this intake to {selectedClient.firstName}{" "}
                {selectedClient.lastName} ({selectedClient.email})?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowConfirmLinkModal(false)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={handleConfirmLink}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-700">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateClientModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-sm shadow-xl">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Create Client from Intake
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="createParents"
                    checked={createParents}
                    onChange={(e) => setCreateParents(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                  <label
                    htmlFor="createParents"
                    className="text-sm text-gray-700">
                    Also create parent(s)
                  </label>
                </div>

                <div className="text-sm text-gray-600">
                  <p>This will create:</p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>
                      A new client record for {intake.client.firstName}{" "}
                      {intake.client.lastName}
                    </li>
                    {createParents && (
                      <>
                        {intake.parentGuardian1?.firstName && (
                          <li>
                            A parent record for{" "}
                            {intake.parentGuardian1.firstName}{" "}
                            {intake.parentGuardian1.surname}
                          </li>
                        )}
                        {intake.parentGuardian2?.firstName && (
                          <li>
                            A parent record for{" "}
                            {intake.parentGuardian2.firstName}{" "}
                            {intake.parentGuardian2.surname}
                          </li>
                        )}
                      </>
                    )}
                  </ul>
                  <p className="mt-3">And automatically:</p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Link this intake form to the new client record</li>
                    {createParents && (intake.parentGuardian1?.firstName || intake.parentGuardian2?.firstName) && (
                      <li>
                        Establish parent-child relationships between the new client and any created parent records
                      </li>
                    )}
                  </ul>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setShowCreateClientModal(false)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateClientFromIntake}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-700">
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IntakeDetailsModal;
